/**
 * Time Grid
 *
 * Main container for the time-slot calendar view.
 * Composes: TimeGutter + TimeGridHeader + TimeColumns + AllDaySection.
 * Handles scheduling DnD via useTimeGridDrag.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import { startOfDay } from 'date-fns';
import { TimeGutter } from './TimeGutter';
import { TimeGridHeader } from './TimeGridHeader';
import { TimeColumn } from './TimeColumn';
import { AllDaySection } from './AllDaySection';
import { QuickSchedulePopover } from './QuickSchedulePopover';
import { UnscheduledTasksPanel } from './UnscheduledTasksPanel';
import { useScheduler, DEFAULT_DURATION_MS } from '@/hooks/useScheduler';
import { useTimeGridDrag } from '@/hooks/useTimeGridDrag';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR, SLOT_HEIGHT_PX } from '@/utils/scheduleLayout';
import type { Task } from '@/types';
import type { PositionedItem } from '@/hooks/useScheduler';

interface TimeGridProps {
  tasks: Task[];
  days: Date[];
  calendarEvents?: Array<{
    id: string;
    summary: string | null;
    startTime: number;
    endTime: number;
    allDay: boolean;
    colorId: string | null;
    linkedTaskId: string | null;
    status: string;
    htmlLink: string | null;
  }>;
  startHour?: number;
  endHour?: number;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onCreateTask: (taskData: Partial<Task>) => void;
  onTaskClick?: (task: Task) => void;
}

export function TimeGrid({
  tasks,
  days,
  calendarEvents = [],
  startHour = DEFAULT_START_HOUR,
  endHour = DEFAULT_END_HOUR,
  onUpdateTask,
  onCreateTask,
  onTaskClick,
}: TimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Popover state for quick-scheduling
  const [popover, setPopover] = useState<{
    date: Date;
    startTimeMs: number;
    endTimeMs: number;
  } | null>(null);

  // Scheduler: merge tasks + events, compute layout
  const { dayColumns, allDayItems, unscheduledTasks } = useScheduler({
    tasks,
    calendarEvents,
    days,
    startHour,
    endHour,
  });

  // Drag handler
  const handleUpdateSchedule = useCallback(
    (itemId: string, scheduledStart: number, scheduledEnd: number) => {
      // Strip gcal- prefix for calendar events (read-only, shouldn't reach here)
      if (itemId.startsWith('gcal-')) return;
      onUpdateTask(itemId, { scheduledStart, scheduledEnd });
    },
    [onUpdateTask]
  );

  const handleSlotClick = useCallback(
    (date: Date, timeMs: number) => {
      setPopover({
        date,
        startTimeMs: timeMs,
        endTimeMs: timeMs + DEFAULT_DURATION_MS,
      });
    },
    []
  );

  const {
    dragState,
    isDragging,
    handleItemPointerDown,
    handleResizePointerDown,
    handleSlotPointerDown,
    ghostPosition,
  } = useTimeGridDrag({
    startHour,
    onUpdateSchedule: handleUpdateSchedule,
    onSlotClick: handleSlotClick,
  });

  // Scroll to current time on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    const now = new Date();
    const hoursSinceStart = now.getHours() - startHour;
    if (hoursSinceStart > 0) {
      const scrollTop = hoursSinceStart * 2 * SLOT_HEIGHT_PX - SLOT_HEIGHT_PX * 2;
      scrollRef.current.scrollTop = Math.max(0, scrollTop);
    }
  }, [startHour]);

  // Handle quick create from popover
  const handlePopoverCreate = useCallback(
    (name: string, scheduledStart: number, scheduledEnd: number) => {
      if (!popover) return;
      onCreateTask({
        name,
        scheduledStart,
        scheduledEnd,
        dueDate: startOfDay(popover.date).getTime(),
      });
      setPopover(null);
    },
    [popover, onCreateTask]
  );

  const handleDropUnscheduledTask = useCallback(
    (taskId: string, scheduledStart: number, scheduledEnd: number) => {
      onUpdateTask(taskId, { scheduledStart, scheduledEnd });
    },
    [onUpdateTask]
  );

  const handleItemClick = useCallback(
    (item: PositionedItem) => {
      if (item.type === 'task' && item.taskData && onTaskClick) {
        onTaskClick(item.taskData as Task);
      } else if (item.type === 'calendar_event' && item.eventData) {
        // For Google Calendar events, open the HTML link if available
        const event = item.eventData as { htmlLink?: string };
        if (event.htmlLink) {
          window.open(event.htmlLink, '_blank', 'noopener,noreferrer');
        }
      }
    },
    [onTaskClick]
  );

  return (
    <div className="h-full flex flex-col bg-surface-secondary/30 rounded-xl border border-border-primary overflow-hidden">
      {/* Header */}
      <TimeGridHeader days={days} />

      {/* All Day Section */}
      <AllDaySection
        days={days}
        items={allDayItems}
        onItemClick={(item) => {
          if (item.type === 'task' && item.taskData && onTaskClick) {
            onTaskClick(item.taskData as Task);
          }
        }}
      />

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto relative">
        <div className="flex">
          {/* Unscheduled tasks panel */}
          <UnscheduledTasksPanel
            tasks={unscheduledTasks}
            onTaskClick={onTaskClick}
          />

          {/* Time gutter */}
          <TimeGutter startHour={startHour} endHour={endHour} />

          {/* Day columns */}
          {days.map((day) => {
            const dayKey = day.toISOString();
            const items = dayColumns.get(dayKey) || [];
            const isDragDay = dragState?.dayDate.toISOString() === dayKey;

            return (
              <TimeColumn
                key={dayKey}
                day={day}
                items={items}
                startHour={startHour}
                endHour={endHour}
                dragItemId={isDragging ? dragState?.itemId : null}
                ghostTop={isDragDay ? ghostPosition?.top : null}
                ghostHeight={isDragDay ? ghostPosition?.height : null}
                onItemPointerDown={handleItemPointerDown}
                onResizePointerDown={handleResizePointerDown}
                onSlotPointerDown={handleSlotPointerDown}
                onItemClick={handleItemClick}
                onDropUnscheduledTask={handleDropUnscheduledTask}
              />
            );
          })}
        </div>

        {/* Quick schedule popover */}
        {popover && (
          <QuickSchedulePopover
            isOpen
            date={popover.date}
            startTimeMs={popover.startTimeMs}
            endTimeMs={popover.endTimeMs}
            onClose={() => setPopover(null)}
            onCreateTask={handlePopoverCreate}
          />
        )}
      </div>
    </div>
  );
}
